】<template>
  <div class="game-container" @keydown="handleKeyPress" tabindex="0">
    <!-- 悬浮的得分和正确率面板 -->
    <div class="floating-stats">
      <div class="stat-card score">
        <div class="card-decoration">
          <div class="decoration-circle"></div>
          <div class="decoration-line"></div>
        </div>
        <div class="stat-box score">
          <div class="stat-icon">
            <span class="icon-circle">🎯</span>
          </div>
          <div class="stat-content">
            <div class="stat-value-container">
              <div class="stat-value">{{ score }}</div>
              <div class="stat-value-effect" v-if="scoreChange > 0"></div>
              <div class="stat-label">得分</div>
            </div>
          </div>
          <div class="score-change" v-if="scoreChange !== 0" :class="{ 'positive': scoreChange > 0, 'negative': scoreChange < 0 }">
            {{ scoreChange > 0 ? '+' + scoreChange : scoreChange }}
          </div>
        </div>
      </div>
      <div class="stat-card accuracy">
        <div class="card-decoration">
          <div class="decoration-circle"></div>
          <div class="decoration-line"></div>
        </div>
        <div class="stat-box accuracy">
          <div class="stat-icon">
            <span class="icon-circle">✨</span>
          </div>
          <div class="stat-content">
            <div class="stat-value-container">
              <div class="stat-value">{{ accuracy }}%</div>
              <div class="accuracy-bar">
                <div class="accuracy-fill" :style="{ width: accuracy + '%' }" :class="{ 'high': accuracy >= 90, 'medium': accuracy >= 70 && accuracy < 90, 'low': accuracy < 70 }"></div>
              </div>
            </div>
            <div class="stat-label">正确率</div>
          </div>
        </div>
      </div>
    </div>
    <div class="game-header">
      <div class="header-content">
        <h1>{{ title }}</h1>
        <p class="instruction">{{ instruction }}</p>
      </div>
      
      <!-- 添加进度条到顶部 -->
      <div class="progress-row">
        <div class="level-info">进度: {{ Math.round(progress) }}%</div>
        <div class="progress">
          <div class="progress-bar" :style="{ width: progress + '%' }"></div>
        </div>
      </div>
    </div>

    <div class="game-content">
      <div class="typing-area" :class="{ correct: isCorrect, wrong: isWrong }">
        <div v-if="currentKeyword" class="keyword-info">
          <!-- 正向模式：显示词汇和描述 -->
          <template v-if="!isReverseMode">
            <div class="keyword-name">{{ currentKeyword.name }}</div>
            <div class="keyword-desc">{{ currentKeyword.desc }}</div>
          </template>
          <!-- 反向模式：显示描述作为提示 -->
          <template v-else>
            <div class="keyword-question">这个词汇是什么?</div>
            <div class="keyword-name reverse">{{ currentKeyword.desc }}</div>
            <div class="keyword-hint" v-if="showHint">
              <span class="hint-label">提示:</span> 
              <span class="hint-text">首字母 "{{ currentKeyword.name[0] }}"</span>
            </div>
          </template>
          <div class="audio-hint">按空格键听发音</div>
        </div>
        <!-- 正向模式显示字母提示 -->
        <div v-if="!isReverseMode" class="target-letters">
          <span 
            v-for="(letter, index) in targetLetters" 
            :key="index"
            :class="{
              'current': index === currentIndex,
              'completed': index < currentIndex
            }"
          >{{ letter }}</span>
        </div>
        <!-- 反向模式显示用户输入的内容 -->
        <div v-else class="user-input">
          <span class="input-prompt">请输入对应的词汇：</span>
          <div class="input-display">
            <span class="typed-text">{{ typedText }}</span>
            <span class="cursor-indicator"></span>
          </div>
        </div>
        <!-- 正向模式显示当前字母指示 -->
        <div v-if="!isReverseMode" class="letter-instruction">
          请输入 
          <span class="letter-highlight" :class="{ 'uppercase': /[A-Z]/.test(currentLetter), 'special-char': /[!@#$%^&*()_+{}|:<>?]/.test(currentLetter) }">
            {{ currentLetter }}
            <span v-if="/[A-Z]/.test(currentLetter)" class="uppercase-hint">大写</span>
            <span v-else-if="/[!@#$%^&*()_+{}|:<>?]/.test(currentLetter)" class="special-char-hint">按住Shift</span>
            <span v-else-if="currentLetter === 'ü'" class="special-char-hint">按v键</span>
          </span>
        </div>
        <!-- 反向模式添加提示按钮 -->
        <div v-else class="hint-container">
          <button @click="toggleHint" class="hint-button">
            <span class="button-icon">💡</span>
            {{ showHint ? '隐藏提示' : '显示提示' }}
          </button>
        </div>
      </div>

      <!-- 添加模式切换按钮 -->
      <div class="mode-switch-container">
        <button 
          disabled
          class="mode-switch-button disabled"
        >
          <span class="button-icon">🔒</span>
          词汇拼写
        </button>
        <div class="mode-description">
          输入完整拼写
        </div>
      </div>

      <div class="practice-keyboard">
        <div class="practice-row">
          <div 
            v-for="(key, index) in ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || (key === '1' && currentLetter === '!') || (key === '2' && currentLetter === '@') || (key === '3' && currentLetter === '#') || (key === '4' && currentLetter === '$') || (key === '5' && currentLetter === '%') || (key === '6' && currentLetter === '^') || (key === '7' && currentLetter === '&') || (key === '8' && currentLetter === '*') || (key === '9' && currentLetter === '(') || (key === '0' && currentLetter === ')') || (key === '-' && currentLetter === '_') || (key === '=' && currentLetter === '+') || (key === '`' && currentLetter === '~')) }"
          >
            <span class="key-top">{{ index === 0 ? '~' : index === 1 ? '!' : index === 2 ? '@' : index === 3 ? '#' : index === 4 ? '$' : index === 5 ? '%' : index === 6 ? '^' : index === 7 ? '&' : index === 8 ? '*' : index === 9 ? '(' : index === 10 ? ')' : index === 11 ? '_' : '+' }}</span>
            <span class="key-bottom">{{ key }}</span>
          </div>
          <div class="practice-key backspace-key">←</div>
        </div>
        <div class="practice-row">
          <div class="practice-key tab-key">Tab</div>
          <div 
            v-for="key in ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', '[', ']']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || currentLetter === key.toLowerCase() || (key === '[' && currentLetter === '{') || (key === ']' && currentLetter === '}')) }"
          >
            <span v-if="key === '[' || key === ']'">
              <span class="key-top">{{ key === '[' ? '{' : '}' }}</span>
              <span class="key-bottom">{{ key }}</span>
            </span>
            <span v-else class="key-single">{{ key }}</span>
          </div>
          <div class="practice-key backslash-key">
            <span class="key-top">|</span>
            <span class="key-bottom">\</span>
          </div>
        </div>
        <div class="practice-row">
          <div class="practice-key caps-key">Caps</div>
          <div 
            v-for="key in ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ';', '\'']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || currentLetter === key.toLowerCase() || (key === ';' && currentLetter === ':') || (key === '\'' && currentLetter === '\'')) }"
          >
            <span v-if="key === ';' || key === '\''">
              <span class="key-top">{{ key === ';' ? ':' : '&quot;' }}</span>
              <span class="key-bottom">{{ key }}</span>
            </span>
            <span v-else class="key-single">{{ key }}</span>
          </div>
          <div class="practice-key enter-key">Enter</div>
        </div>
        <div class="practice-row">
          <div class="practice-key shift-key" :class="{ 'active-shift': !isReverseMode && isShiftRequired(currentLetter) }">Shift</div>
          <div 
            v-for="key in ['Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || currentLetter === key.toLowerCase() || (key === ',' && currentLetter === '<') || (key === '.' && currentLetter === '>') || (key === '/' && currentLetter === '?') || (key === 'V' && currentLetter === 'ü')) }"
          >
            <span v-if="key === ',' || key === '.' || key === '/'">
              <span class="key-top">{{ key === ',' ? '&lt;' : key === '.' ? '&gt;' : '?' }}</span>
              <span class="key-bottom">{{ key }}</span>
            </span>
            <span v-else-if="key === 'V'" class="key-single">{{ key }}{{ currentLetter === 'ü' ? '(ü)' : '' }}</span>
            <span v-else class="key-single">{{ key }}</span>
          </div>
          <div class="practice-key shift-key-right" :class="{ 'active-shift': !isReverseMode && isShiftRequired(currentLetter) }">Shift</div>
        </div>
        <div class="practice-row">
          <div class="practice-key ctrl-key">Ctrl</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key space-key" :class="{ 'active': !isReverseMode && currentLetter === ' ' }">空格</div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key menu-key">Menu</div>
          <div class="practice-key ctrl-key">Ctrl</div>
        </div>
      </div>

     
      
      <!-- 添加退出按钮 -->
      <div class="control-buttons">
        <button @click="exitPractice" class="exit-button">
          <span class="button-icon">🚪</span>
          退出练习
        </button>
      </div>
    </div>
    <!-- 添加打字报告弹窗 -->
    <div class="report-modal" v-if="showComplete">
      <div class="report-content">
        <h2>{{ title }}练习报告</h2>
        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-label">练习用时</div>
            <div class="stat-value">{{ formatTime(totalTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">最终得分</div>
            <div class="stat-value">{{ score }}分</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ accuracy }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">打字速度</div>
            <div class="stat-value">{{ typingSpeed }}个/分钟</div>
          </div>
        </div>
        
        <div class="report-message">
          <div class="result-container">
            <img :src="resultImage" :alt="resultMessage">
            <p>{{ resultMessage }}</p>
          </div>
        </div>
        
        <div class="report-buttons">
          <button @click="restartLevel">关闭</button>
        </div>
      </div>
    </div>

    
  </div>
</template>

<script>
export default {
  name: 'TypingPlanetPin',
  data() {
    return {
      gameStarted: true,
      currentLetter: '',
      targetLetters: '',
      currentIndex: 0,
      score: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      accuracy: 100,
      isCorrect: false,
      isWrong: false,
      progress: 0,
      showComplete: false,
      combinations: 0,
      scoreChange: 0,
      scoreChangeTimeout: null,
      title: '',
      instruction: '',
      levelId: '',
      nextLevel: '',
      startTime: null,
      totalTime: 0,
      typingSpeed: 0,
      resultMessage: '',
      resultImage: '',
      targetGroup: 'group1', // 默认分组
      currentKeyword: null, // 当前词汇对象
      resultImages: {
        excellent: 'https://wugecdn.steam.fun/typefun/images/excellent.png',
        good: 'https://wugecdn.steam.fun/typefun/images/good.png',
        keepTrying: 'https://wugecdn.steam.fun/typefun/images/keep-trying.png'
      },
      keywords: [
        {
          name:'b',
          desc:'发音时双唇紧闭，然后迅速分开，将空气从口中释放出来。',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/b.mp3',
          group:'group1'
        },
        {
          name:'p',
          desc:"发音时双唇紧闭，然后迅速分开，将空气从口中释放出来，与'b'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/p.mp3',
          group:'group1'
        },
        {
          name:'m',
          desc:"发音时双唇紧闭，将声带震动，然后迅速分开，将空气从口中释放出来。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/m.mp3',
          group:'group1'
        },
        {
          name:'f',
          desc:"用上齿轻触下唇，然后将空气呼出。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/f.mp3',
          group:'group1'
        },
        {
          name:'d',
          desc:"将舌头接触上齿龈，然后突然分离，使空气发生爆破。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/d.mp3',
          group:'group1'
        },
        {
          name:'t',
          desc:"将舌头接触上齿龈，然后突然分离，使空气发生爆破，与'd'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/t.mp3',
          group:'group1'
        },
        {
          name:'n',
          desc:"将舌头接触上齿龈，然后突然分离，使空气发生爆破，同时声带震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/n.mp3',
          group:'group1'
        },
        {
          name:'l',
          desc:"舌尖接触上齿龈，稍微卷曲，空气由舌尖两侧流出。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/l.mp3',
          group:'group1'
        },
        {
          name:'g',
          desc:"舌头的后部与软腭接触，然后突然分离，空气通过口腔释放。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/g.mp3',
          group:'group1'
        },
        {
          name:'k',  
          desc:"舌头的后部与软腭接触，然后突然分离，空气通过口腔释放，与'g'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/k.mp3',
          group:'group1'
        },
        {
          name:'h',
          desc:"敞开口腔，将空气由喉咙吹出。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/h.mp3',
          group:'group1'
        },
        {
          name:'j',
          desc:"舌尖接触上齿龈，稍微卷曲，然后迅速分开，使空气发生爆破。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/j.mp3',
          group:'group1'
        },
        {
          name:'q',
          desc:"舌尖接触上齿龈，稍微卷曲，然后迅速分开，使空气发生爆破，与'j'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/q.mp3',
          group:'group1'
        },
        {
          name:'x',
          desc:"舌尖接触上齿龈，稍微卷曲，将舌尖迅速从前起始点向后移动，使空气发生摩擦音。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/x.mp3',
          group:'group1'
        },
        {
          name:'zh',
          desc:"舌尖接触上齿龈，然后抬起，形成狭小的通道，空气挤过通道时发出摩擦音。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/zh.mp3',
          group:'group1'
        },
        {
          name:'ch',
          desc:"舌尖接触上齿龈，然后抬起，形成狭小的通道，空气挤过通道时发出摩擦音，与'zh'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/ch.mp3',
          group:'group1'
        },
        {
          name:'sh',
          desc:"舌尖接触上齿龈，然后抬起，形成狭小的通道，空气挤过通道时发出摩擦音，与'zh'和'ch'的区别在于通道更为狭小。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/sh.mp3',
          group:'group1'
        },
        {
          name:'r',
          desc:"舌尖不接触上齿龈，舌根卷曲，空气呼出时使舌根振动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/r.mp3',
          group:'group1'
        },
        {
          name:'z',
          desc:"舌尖接触上齿龈，然后迅速分开，使空气发生爆破。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/z.mp3',
          group:'group1'
        },
        {
          name:'c',
          desc:"舌尖接触上齿龈，然后迅速分开，使空气发生爆破，与'z'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/c.mp3',
          group:'group1'
        },
        {
          name:'s',
          desc:"舌尖接触上齿龈，然后迅速分开，使空气发生爆破，与'z'和'c'的区别在于声带不震动。",
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E5%A3%B0%E6%AF%8D/s.mp3',
          group:'group1'
        },
        {
          name:'a',
          desc:'发音时嘴巴张开，舌头平放，声音从喉咙发出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/a.mp3',
          group:'group2'
        },
        {
          name:'o',
          desc:'发音时嘴唇呈圆形，舌后部抬起，声音从喉咙发出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/o.mp3',
          group:'group2'
        },
        {
          name:'e',
          desc:'发音时嘴微张，舌头稍微抬起，声音从喉咙发出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/e.mp3',
          group:'group2'
        },
        {
          name:'i',
          desc:'发音时嘴微微张开，舌头前部抬高接近上颚',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/i.mp3',
          group:'group2'
        },
        {
          name:'u',
          desc:'发音时嘴唇呈圆形向前突出，舌后部抬高',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/u.mp3',
          group:'group2'
        },
        {
          name:'ü',
          desc:'发音时嘴唇呈圆形向前突出，舌前部抬高',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/u%CC%88.mp3', 
          group:'group2'
        },
        {
          name:'ai',
          desc:'由a到i的连续发音，舌位由低到高变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ai.mp3',
          group:'group2'
        },
        {
          name:'ei',
          desc:'由e到i的连续发音，舌位由中到高变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ei.mp3',
          group:'group2'
        },
        {
          name:'ao',
          desc:'由a到o的连续发音，舌位由低到高变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ao.mp3',
          group:'group2'
        },
        {
          name:'ou',
          desc:'由o到u的连续发音，舌位由中到高变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ou.mp3',
          group:'group2'
        },
        {
          name:'iu',
          desc:'由i到u的连续发音，舌位由前高到后高变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/iu.mp3',
          group:'group2'
        },
        {
          name:'ie',
          desc:'由i到e的连续发音，舌位由高到中变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ie.mp3',
          group:'group2'
        },
        {
          name:'üe',
          desc:'由ü到e的连续发音，舌位由高到中变化',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/u%CC%88e.mp3',
          group:'group2'
        },
        {
          name:'er',
          desc:'发音时舌尖向上卷起，声音从喉咙发出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/er.mp3',
          group:'group2'
        },
        {
          name:'an',
          desc:'a音后加鼻音n，气流从口腔和鼻腔同时通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/an.mp3',
          group:'group2'
        },
        {
          name:'en',
          desc:'e音后加鼻音n，气流从口腔和鼻腔同时通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/en.mp3',
          group:'group2'
        },
        {
          name:'in',
          desc:'i音后加鼻音n，气流从口腔和鼻腔同时通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/in.mp3',
          group:'group2'
        },
        {
          name:'un',
          desc:'u音后加鼻音n，气流从口腔和鼻腔同时通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/un.mp3',
          group:'group2'
        },
        {
          name:'ün',
          desc:'ü音后加鼻音n，气流从口腔和鼻腔同时通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/u%CC%88n.mp3',
          group:'group2'
        },
        {
          name:'ang',
          desc:'a音后加鼻音ng，气流主要从鼻腔通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ang.mp3',
          group:'group2'
        },
        {
          name:'eng',
          desc:'e音后加鼻音ng，气流主要从鼻腔通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/eng.mp3',
          group:'group2'
        },
        {
          name:'ing',
          desc:'i音后加鼻音ng，气流主要从鼻腔通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ing.mp3',
          group:'group2'
        },
        {
          name:'ong',
          desc:'o音后加鼻音ng，气流主要从鼻腔通过',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/ong.mp3',
          group:'group2'
        },
        {
          name:'zhi',
          desc:'整体认读音节，舌尖后音zh与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/zh.mp3',
          group:'group3'
        },
        {
          name:'chi',
          desc:'整体认读音节，舌尖后音ch与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/ch.mp3',
          group:'group3'
        },
        {
          name:'shi',
          desc:'整体认读音节，舌尖后音sh与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/sh.mp3',
          group:'group3'
        },
        {
          name:'ri',
          desc:'整体认读音节，舌尖后音r与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/r.mp3',
          group:'group3'
        },
        {
          name:'zi',
          desc:'整体认读音节，舌尖前音z与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/z.mp3',
          group:'group3'
        },
        {
          name:'ci',
          desc:'整体认读音节，舌尖前音c与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/c.mp3',
          group:'group3'
        },
        {
          name:'si',
          desc:'整体认读音节，舌尖前音s与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/s.mp3',
          group:'group3'
        },
        {
          name:'yi',
          desc:'整体认读音节，舌尖前音y与韵母i组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/y.mp3',
          group:'group3'
        },
        {
          name:'wu',
          desc:'整体认读音节，舌尖前音w与韵母u组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/w.mp3',
          group:'group3'
        },
        {
          name:'yu',
          desc:'整体认读音节，舌尖前音y与韵母u组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/yu.mp3',
          group:'group3'
        },
        {
          name:'ye',
          desc:'整体认读音节，舌尖前音y与韵母e组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/ie.mp3',
          group:'group3'
        },
        {
          name:'yue',
          desc:'整体认读音节，舌尖前音y与韵母ue组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/u%CC%88e.mp3',
          group:'group3'
        },
        {
          name:'yuan',
          desc:'整体认读音节，舌尖前音y与韵母uan组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E9%9F%B5%E6%AF%8D/yuan.mp3',
          group:'group3'
        },
        {
          name:'yin',
          desc:'整体认读音节，舌尖前音y与韵母in组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/in.mp3',
          group:'group3'
        },
        {
          name:'yun',
          desc:'整体认读音节，舌尖前音y与韵母un组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/yuan11.mp3',
          group:'group3'
        },
        {
          name:'yuan',
          desc:'整体认读音节，舌尖前音y与韵母uan组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/yuan1.mp3',
          group:'group3'
        },

        {
          name:'ying',
          desc:'整体认读音节，舌尖前音y与韵母ing组合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/%E6%95%B4%E4%BD%93%E8%AE%A4%E8%AF%BB%E9%9F%B3%E8%8A%82/in.mp3',
          group:'group3'
        },

        {
          name:'ba',
          desc:'巴, 八',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/ba.mp3',
          group:'group4'
        },

        {
          name:'bo',
          desc:'波, 播',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/bo.mp3',
          group:'group4'
        },
        {
          name:'bu',
          desc:'不, 布',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/bu.mp3',
          group:'group4'
        },
        {
          name:'pa',
          desc:'趴, 怕',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/pa.mp3',
          group:'group4'
        },
        {
          name:'po',
          desc:'破, 坡',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/po.mp3',
          group:'group4'
        },
        {
          name:'pu',
          desc:'普, 葡',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/pu.mp3',
          group:'group4'
        },
        {
          name:'ma',
          desc:'妈, 马',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/ma.mp3',
          group:'group4'
        },
        {
          name:'me',
          desc:'么, 没',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/me.mp3',
          group:'group4'
        },
        {
          name:'mi',
          desc:'米, 密',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/mi.mp3',
          group:'group4'
        },
        {
          name:'mo',
          desc:'模, 末',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/mo.mp3',
          group:'group4'
        },
        {
          name:'fa',
          desc:'发, 法',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/fa.mp3',
          group:'group4'
        },
        {
          name:'fu',
          desc:'夫, 服',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/fu.mp3',
          group:'group4'
        },
        {
          name:'bi',
          desc:'笔, 必',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/bi.mp3',
          group:'group4'
        },
        {
          name:'pi',
          desc:'皮, 匹',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/pi.mp3',
          group:'group4'
        },
        {
          name:'pei',
          desc:'配, 培',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group4/pei.mp3',
          group:'group4'
        },
        {
          name:'ba',
          desc:'吧',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/ba.mp3',
          group:'group5'
        },
        {
          name:'bo',
          desc:'波',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bo.mp3',
          group:'group5'
        },
        {
          name:'bei',
          desc:'杯',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bei.mp3',
          group:'group5'
        },
        {
          name:'bai',
          desc:'白',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bai.mp3',
          group:'group5'
        },
        {
          name:'bu',
          desc:'步',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bu.mp3',
          group:'group5'
        },
        {
          name:'ban',
          desc:'班',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/ban.mp3',
          group:'group5'
        },
        {
          name:'bang',
          desc:'帮',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bang.mp3',
          group:'group5'
        },
        {
          name:'bao',
          desc:'包',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bao.mp3',
          group:'group5'
        },
        {
          name:'bei',
          desc:'北',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bei2.mp3',
          group:'group5'
        },
        {
          name:'bie',
          desc:'别',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bie.mp3',
          group:'group5'
        },
        {
          name:'bi',
          desc:'笔',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bi.mp3',
          group:'group5'
        },
        {
          name:'bing',
          desc:'冰',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bing.mp3',
          group:'group5'
        },
        {
          name:'biao',
          desc:'表',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/biao.mp3',
          group:'group5'
        },
        {
          name:'bui',
          desc:'补',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bui.mp3',
          group:'group5'
        },
        {
          name:'bao',
          desc:'爆',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group5/bao2.mp3',
          group:'group5'
        },
        {
          name:'pa',
          desc:'怕',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pa.mp3',
          group:'group6'
        },
        {
          name:'po',
          desc:'坡',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/po.mp3',
          group:'group6'
        },
        {
          name:'pei',
          desc:'配',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pei.mp3',
          group:'group6'
        },
        {
          name:'pai',
          desc:'拍',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pai.mp3',
          group:'group6'
        },
        {
          name:'pu',
          desc:'普',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pu.mp3',
          group:'group6'
        },
        {
          name:'pan',
          desc:'盘',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pan.mp3',
          group:'group6'
        },
        {
          name:'pang',
          desc:'胖',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pang.mp3',
          group:'group6'
        },
        {
          name:'piao',
          desc:'票',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/piao.mp3',
          group:'group6'
        },
        {
          name:'pie',
          desc:'撇',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pie.mp3',
          group:'group6'
        },
        {
          name:'pi',
          desc:'皮',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pi.mp3',
          group:'group6'
        },
        {
          name:'ping',
          desc:'平',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/ping.mp3',
          group:'group6'
        },
        {
          name:'pian',
          desc:'篇',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pian.mp3',
          group:'group6'
        },
        {
          name:'po',
          desc:'破',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/po2.mp3',
          group:'group6'
        },
        {
          name:'pu',
          desc:'葡',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/pu2.mp3',
          group:'group6'
        },
        {
          name:'puo',
          desc:'迫',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group6/puo.mp3',
          group:'group6'
        },

        {
          name:'ma',
          desc:'妈',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/ma.mp3',
          group:'group7'
        },
        {
          name:'mo',
          desc:'摸',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mo.mp3',
          group:'group7'
        },
        {
          name:'mei',
          desc:'美',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mei.mp3',
          group:'group7'
        },
        {
          name:'mai',
          desc:'买',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mai.mp3',
          group:'group7'
        },
        {
          name:'mu',
          desc:'木',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mu.mp3',
          group:'group7'
        },
        {
          name:'man',
          desc:'满',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/man.mp3',
          group:'group7'
        },
        {
          name:'mang',
          desc:'忙',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mang.mp3',
          group:'group7'
        },
        {
          name:'mao',
          desc:'猫',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mao.mp3',
          group:'group7'
        },
        {
          name:'mie',
          desc:'灭',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mie.mp3',
          group:'group7'
        },
        {
          name:'mi',
          desc:'米',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mi.mp3',
          group:'group7'
        },
        {
          name:'ming',
          desc:'名',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/ming.mp3',
          group:'group7'
        },
        {
          name:'mian',
          desc:'面',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mian.mp3',
          group:'group7'
        },
        {
          name:'mo',
          desc:'魔',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mo2.mp3',
          group:'group7'
        },
        {
          name:'mu',
          desc:'目',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/mu2.mp3',
          group:'group7'
        },
        {
          name:'meng',
          desc:'梦',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group7/meu.mp3',
          group:'group7'
        },
        {
          name:'fa',
          desc:'发',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fa.mp3',
          group:'group8'
        },
        {
          name:'fo',
          desc:'佛',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fo.mp3',
          group:'group8'
        },
        {
          name:'fei',
          desc:'肥',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fei.mp3',
          group:'group8'
        },
        {
          name:'fei',
          desc:'菲',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fei.mp3',
          group:'group8'
        },
        {
          name:'fu',
          desc:'富',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fu.mp3',
          group:'group8'
        },
        {
          name:'fan',
          desc:'饭',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fan.mp3',
          group:'group8'
        },
        {
          name:'fang',
          desc:'方',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fang.mp3',
          group:'group8'
        },
        {
          name:'fei',
          desc:'飞',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fei2.mp3',
          group:'group8'
        },
        {
          name:'fou',
          desc:'否',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fou.mp3',
          group:'group8'
        },
        {
          name:'fen',
          desc:'粉',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fen.mp3',
          group:'group8'
        },
        {
          name:'feng',
          desc:'风',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/feng.mp3',
          group:'group8'
        },
        {
          name:'feng',
          desc:'疯',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/feng.mp3',
          group:'group8'
        },
        {
          name:'fei',
          desc:'废',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fei3.mp3',
          group:'group8'
        },
        {
          name:'feng',
          desc:'封',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/feng2.mp3',
          group:'group8'
        },
        {
          name:'fu',
          desc:'福',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group8/fu.mp3',
          group:'group8'
        },

        {
          name:'ba',
          desc:'吧',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/ba.mp3',
          group:'group9'
        },
        {
          name:'bo',
          desc:'波',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/bo.mp3',
          group:'group9'
        },
        {
          name:'bi',
          desc:'笔',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/bi.mp3',
          group:'group9'
        },
        {
          name:'pan',
          desc:'判',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/pan.mp3',
          group:'group9'
        },
        {
          name:'ping',
          desc:'评',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/ping.mp3',
          group:'group9'
        },
        {
          name:'pou',
          desc:'剖',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/pou.mp3',
          group:'group9'
        },
        {
          name:'mou',
          desc:'某',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/mou.mp3',
          group:'group9'
        },
        {
          name:'fan',
          desc:'繁',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/fan.mp3',
          group:'group9'
        },
        {
          name:'fu',
          desc:'付',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/fu.mp3',
          group:'group9'
        },
        {
          name:'bai',
          desc:'百',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/bai.mp3',
          group:'group9'
        },
        {
          name:'pi',
          desc:'皮',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/pi.mp3',
          group:'group9'
        },
        {
          name:'mei',
          desc:'美',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/mei.mp3',
          group:'group9'
        },
        {
          name:'fa',
          desc:'法',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/fa.mp3',
          group:'group9'
        },
        {
          name:'feng',
          desc:'风',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/feng.mp3',
          group:'group9'
        },
        {
          name:'mu',
          desc:'木',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group9/mu.mp3',
          group:'group9'
        },
        {
          name:'da',
          desc:'答',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/da.mp3',
          group:'group10'
        },
        {
          name:'di',
          desc:'地',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/di.mp3',
          group:'group10'
        },
        {
          name:'tai',
          desc:'太',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/tai.mp3',
          group:'group10'
        },
        {
          name:'duo',
          desc:'多',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/duo.mp3',
          group:'group10'
        },
        {
          name:'tuan',
          desc:'团',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/tuan.mp3',
          group:'group10'
        },
        {
          name:'na',
          desc:'拿',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/na.mp3',
          group:'group10'
        },
        {
          name:'li',
          desc:'力',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/li.mp3',
          group:'group10'
        },
        {
          name:'luo',
          desc:'落',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/luo.mp3',
          group:'group10'
        },
        {
          name:'dan',
          desc:'蛋',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/dan.mp3',
          group:'group10'
        },
        {
          name:'tang',
          desc:'糖',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/tang.mp3',
          group:'group10'
        },
        {
          name:'lan',
          desc:'蓝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/lan.mp3',
          group:'group10'
        },
        {
          name:'luan',
          desc:'乱',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/luan.mp3',
          group:'group10'
        },
        {
          name:'tan',
          desc:'探',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/tan.mp3',
          group:'group10'
        },
        {
          name:'ni',
          desc:'你',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/ni.mp3',
          group:'group10'
        },
        {
          name:'lei',
          desc:'雷',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group10/lei.mp3',
          group:'group10'
        },

        {
          name:'u',
          desc:'u',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/u.mp3',
          group:'group11'
        },
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ü.mp3',
          group:'group11'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ou.mp3',
          group:'group11'
        },
        {
          name:'u',
          desc:'u',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/u.mp3',
          group:'group11'
        },
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ü.mp3',
          group:'group11'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ou.mp3',
          group:'group11'
        },
        {
          name:'u',
          desc:'u',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/u.mp3',
          group:'group11'
        },
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ü.mp3',
          group:'group11'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ou.mp3',
          group:'group11'
        },
        {
          name:'u',
          desc:'u',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/u.mp3',
          group:'group11'
        },
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ü.mp3',
          group:'group11'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group11/ou.mp3',
          group:'group11'
        },
        {
          name:'da',
          desc:'大',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/da.mp3',
          group:'group12'
        },
        {
          name:'de',
          desc:'的',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/de.mp3',
          group:'group12'
        },
        {
          name:'di',
          desc:'地',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/di.mp3',
          group:'group12'
        },
        {
          name:'du',
          desc:'度',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/du.mp3',
          group:'group12'
        },
        {
          name:'dao',
          desc:'到',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/dao.mp3',
          group:'group12'
        },
        {
          name:'dou',
          desc:'豆',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/dou.mp3',
          group:'group12'
        },
        {
          name:'dai',
          desc:'带',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/dai.mp3',
          group:'group12'
        },
        {
          name:'die',
          desc:'蝶',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/die.mp3',
          group:'group12'
        },
        {
          name:'dui',
          desc:'对',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/dui.mp3',
          group:'group12'
        },
        {
          name:'duo',
          desc:'多',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/duo.mp3',
          group:'group12'
        },
        {
          name:'ding',
          desc:'顶',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/ding.mp3',
          group:'group12'
        },
        {
          name:'dong',
          desc:'东',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/dong.mp3',
          group:'group12'
        },
        {
          name:'duan',
          desc:'短',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/duan.mp3',
          group:'group12'
        },
        {
          name:'dian',
          desc:'点',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/dian.mp3',
          group:'group12'
        },
        {
          name:'deng',
          desc:'灯',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group12/deng.mp3',
          group:'group12'
        },
        {
          name:'ta',
          desc:'他',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/ta.mp3',
          group:'group13'
        },
        {
          name:'te',
          desc:'特',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/te.mp3',
          group:'group13'
        },
        {
          name:'ti',
          desc:'题',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/ti.mp3',
          group:'group13'
        },
        {
          name:'tu',
          desc:'图',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tu.mp3',
          group:'group13'
        },
        {
          name:'tao',
          desc:'桃',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tao.mp3',
          group:'group13'
        },
        {
          name:'tou',
          desc:'头',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tou.mp3',
          group:'group13'
        },
        {
          name:'tai',
          desc:'台',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tai.mp3',
          group:'group13'
        },
        {
          name:'tie',
          desc:'铁',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tie.mp3',
          group:'group13'
        },
        {
          name:'tui',
          desc:'推',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tui.mp3',
          group:'group13'
        },
        {
          name:'tuo',
          desc:'托',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tuo.mp3',
          group:'group13'
        },
        {
          name:'ting',
          desc:'听',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/ting.mp3',
          group:'group13'
        },
        {
          name:'tong',
          desc:'通',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tong.mp3',
          group:'group13'
        },
        {
          name:'tan',
          desc:'谈',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tan.mp3',
          group:'group13'
        },
        {
          name:'tian',
          desc:'天',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/tian.mp3',
          group:'group13'
        },
        {
          name:'teng',
          desc:'疼',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group13/teng.mp3',
          group:'group13'
        },
        {
          name:'na',
          desc:'拿',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/na.mp3',
          group:'group14'
        },
        {
          name:'ne',
          desc:'呢',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/ne.mp3',
          group:'group14'
        },
        {
          name:'ni',
          desc:'你',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/ni.mp3',
          group:'group14'
        },
        {
          name:'nu',
          desc:'奴',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nu.mp3',
          group:'group14'
        },
        {
          name:'nü',
          desc:'女',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nü.mp3',
          group:'group14'
        },
        {
          name:'nao',
          desc:'脑',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nao.mp3',
          group:'group14'
        },
        {
          name:'ne',
          desc:'ne',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/ne.mp3',
          group:'group14'
        },
        {
          name:'nai',
          desc:'奶',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nai.mp3',
          group:'group14'
        },
        {
          name:'nei',
          desc:'内',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nei.mp3',
          group:'group14'
        },
        {
          name:'nuo',
          desc:'诺',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nuo.mp3',
          group:'group14'
        },
        {
          name:'ning',
          desc:'宁',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/ning.mp3',
          group:'group14'
        },
        {
          name:'nong',
          desc:'农',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nong.mp3',
          group:'group14'
        },
        {
          name:'nan',
          desc:'南',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nan.mp3',
          group:'group14'
        },
        {
          name:'nian',
          desc:'年',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/nian.mp3',
          group:'group14'
        },
        {
          name:'neng',
          desc:'能',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group14/neng.mp3',
          group:'group14'
        },
        {
          name:'la',
          desc:'拉',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/la.mp3',
          group:'group15'
        },
        {
          name:'le',
          desc:'了',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/le.mp3',
          group:'group15'
        },
        {
          name:'li',
          desc:'李',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/li.mp3',
          group:'group15'
        },
        {
          name:'lu',
          desc:'路',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lu.mp3',
          group:'group15'
        },
        {
          name:'lü',
          desc:'绿',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lü.mp3',
          group:'group15'
        },
        {
          name:'lao',
          desc:'老',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lao.mp3',
          group:'group15'
        },
        {
          name:'lou',
          desc:'楼',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lou.mp3',
          group:'group15'
        },
        {
          name:'lai',
          desc:'来',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lai.mp3',
          group:'group15'
        },
        {
          name:'lei',
          desc:'雷',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lei.mp3',
          group:'group15'
        },
        {
          name:'liu',
          desc:'流',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/liu.mp3',
          group:'group15'
        },
        {
          name:'lan',
          desc:'蓝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lan.mp3',
          group:'group15'
        },
        {
          name:'lang',
          desc:'浪',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lang.mp3',
          group:'group15'
        },
        {
          name:'long',
          desc:'龙',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/long.mp3',
          group:'group15'
        },
        {
          name:'lian',
          desc:'连',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/lian.mp3',
          group:'group15'
        },
        {
          name:'leng',
          desc:'冷',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group15/leng.mp3',
          group:'group15'
        },
        {
          name:'da',
          desc:'大',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/da.mp3',
          group:'group16'
        },
        {
          name:'de',
          desc:'的',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/de.mp3',
          group:'group16'
        },
        {
          name:'di',
          desc:'地',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/di.mp3',
          group:'group16'
        },
        {
          name:'du',
          desc:'读',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/du.mp3',
          group:'group16'
        },
        {
          name:'ta',
          desc:'他',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/ta.mp3',
          group:'group16'
        },
        {
          name:'te',
          desc:'特',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/te.mp3',
          group:'group16'
        },
        {
          name:'ti',
          desc:'体',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/ti.mp3',
          group:'group16'
        },
        {
          name:'tu',
          desc:'图',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/tu.mp3',
          group:'group16'
        },
        {
          name:'na',
          desc:'拿',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/na.mp3',
          group:'group16'
        },
        {
          name:'ne',
          desc:'呢',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/ne.mp3',
          group:'group16'
        },
        {
          name:'ni',
          desc:'你',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/ni.mp3',
          group:'group16'
        },
        {
          name:'nu',
          desc:'奴',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/nu.mp3',
          group:'group16'
        },
        {
          name:'la',
          desc:'拉',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/la.mp3',
          group:'group16'
        },
        {
          name:'le',
          desc:'了',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/le.mp3',
          group:'group16'
        },
        {
          name:'li',
          desc:'李',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group16/li.mp3',
          group:'group16'
        },

        {
          name:'zhi',
          desc:'zhi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/zhi.mp3',
          group:'group17'
        },
        {
          name:'chi',
          desc:'chi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/chi.mp3',
          group:'group17'
        },
        {
          name:'shi',
          desc:'是',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/shi.mp3',
          group:'group17'
        },
        {
          name:'ri',
          desc:'日',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/ri.mp3',
          group:'group17'
        },

        {
          name:'zhi',
          desc:'zhi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/zhi.mp3',
          group:'group17'
        },
        {
          name:'chi',
          desc:'chi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/chi.mp3',
          group:'group17'
        },
        {
          name:'shi',
          desc:'是',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/shi.mp3',
          group:'group17'
        },
        {
          name:'ri',
          desc:'日',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/ri.mp3',
          group:'group17'
        },

        {
          name:'zhi',
          desc:'zhi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/zhi.mp3',
          group:'group17'
        },
        {
          name:'chi',
          desc:'chi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/chi.mp3',
          group:'group17'
        },
        {
          name:'shi',
          desc:'是',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/shi.mp3',
          group:'group17'
        },
        {
          name:'ri',
          desc:'日',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group17/ri.mp3',
          group:'group17'
        },

        {
          name:'zi',
          desc:'zi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/zi.mp3',
          group:'group18'
        },
        {
          name:'ci',
          desc:'ci',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/ci.mp3',
          group:'group18'
        },
        {
          name:'si',
          desc:'si',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/si.mp3',
          group:'group18'
        },
        {
          name:'zi',
          desc:'zi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/zi.mp3',
          group:'group18'
        },
        {
          name:'ci',
          desc:'ci',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/ci.mp3',
          group:'group18'
        },
        {
          name:'si',
          desc:'si',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/si.mp3',
          group:'group18'
        },
        {
          name:'zi',
          desc:'zi',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/zi.mp3',
          group:'group18'
        },
        {
          name:'ci',
          desc:'ci',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/ci.mp3',
          group:'group18'
        },
        {
          name:'si',
          desc:'si',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group18/si.mp3',
          group:'group18'
        },
        {
          name:'g',
          desc:'g',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/g.mp3',
          group:'group19'
        },
        {
          name:'k',
          desc:'k',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/k.mp3',
          group:'group19'
        },
        {
          name:'h',
          desc:'h',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/h.mp3',
          group:'group19'
        },
        {
          name:'g',
          desc:'g',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/g.mp3',
          group:'group19'
        },
        {
          name:'k',
          desc:'k',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/k.mp3',
          group:'group19'
        },
        {
          name:'h',
          desc:'h',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/h.mp3',
          group:'group19'
        },
        {
          name:'g',
          desc:'g',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/g.mp3',
          group:'group19'
        },
        {
          name:'k',
          desc:'k',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/k.mp3',
          group:'group19'
        },
        {
          name:'h',
          desc:'h',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group19/h.mp3',
          group:'group19'
        },
        {
          name:'ga',
          desc:'嘎',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/ga.mp3',
          group:'group20'
        },
        {
          name:'ge',
          desc:'个',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/ge.mp3',
          group:'group20'
        },
        {
          name:'gu',
          desc:'古',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gu.mp3',
          group:'group20'
        },
        {
          name:'gai',
          desc:'改',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gai.mp3',
          group:'group20'
        },
        {
          name:'gei',
          desc:'给',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gei.mp3',
          group:'group20'
        },
        {
          name:'gao',
          desc:'高',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gao.mp3',
          group:'group20'
        },
        {
          name:'gou',
          desc:'狗',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gou.mp3',
          group:'group20'
        },
        {
          name:'gan',
          desc:'干',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gan.mp3',
          group:'group20'
        },
        {
          name:'gen',
          desc:'根',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gen.mp3',
          group:'group20'
        },
        {
          name:'gang',
          desc:'刚',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gang.mp3',
          group:'group20'
        },
        {
          name:'geng',
          desc:'更',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/geng.mp3',
          group:'group20'
        },
        {
          name:'gua',
          desc:'瓜',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/gua.mp3',
          group:'group20'
        },
        {
          name:'guo',
          desc:'国',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/guo.mp3',
          group:'group20'
        },
        {
          name:'guan',
          desc:'关',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/guan.mp3',
          group:'group20'
        },
        {
          name:'guang',
          desc:'光',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group20/guang.mp3',
          group:'group20'
        },
        {
          name:'ka',
          desc:'卡',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/ka.mp3',
          group:'group21'
        },
        {
          name:'ke',
          desc:'可',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/ke.mp3',
          group:'group21'
        },
        {
          name:'ku',
          desc:'苦',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/ku.mp3',
          group:'group21'
        },
        {
          name:'kai',
          desc:'开',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kai.mp3',
          group:'group21'
        },
        {
          name:'kao',
          desc:'考',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kao.mp3',
          group:'group21'
        },
        {
          name:'kou',
          desc:'口',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kou.mp3',
          group:'group21'
        },
        {
          name:'kan',
          desc:'看',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kan.mp3',
          group:'group21'
        },
        {
          name:'ken',
          desc:'肯',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/ken.mp3',
          group:'group21'
        },
        {
          name:'kang',
          desc:'康',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kang.mp3',
          group:'group21'
        },
        {
          name:'keng',
          desc:'坑',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/keng.mp3',
          group:'group21'
        },
        {
          name:'kua',
          desc:'夸',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kua.mp3',
          group:'group21'
        },
        {
          name:'kuo',
          desc:'扩',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kuo.mp3',
          group:'group21'
        },
        {
          name:'kuai',
          desc:'快',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kuai.mp3',
          group:'group21'
        },
        {
          name:'kuan',
          desc:'宽',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kuan.mp3',
          group:'group21'
        },
        {
          name:'kuang',
          desc:'狂',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group21/kuang.mp3',
          group:'group21'
        },
        {
          name:'ha',
          desc:'哈',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/ha.mp3',
          group:'group22'
        },
        {
          name:'he',
          desc:'喝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/he.mp3',
          group:'group22'
        },
        {
          name:'hu',
          desc:'湖',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hu.mp3',
          group:'group22'
        },
        {
          name:'hai',
          desc:'海',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hai.mp3',
          group:'group22'
        },
        {
          name:'hei',
          desc:'黑',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hei.mp3',
          group:'group22'
        },
        {
          name:'hao',
          desc:'好',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hao.mp3',
          group:'group22'
        },
        {
          name:'hou',
          desc:'后',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hou.mp3',
          group:'group22'
        },
        {
          name:'han',
          desc:'汉',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/han.mp3',
          group:'group22'
        },
        {
          name:'hen',
          desc:'很',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hen.mp3',
          group:'group22'
        },
        {
          name:'hang',
          desc:'航',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hang.mp3',
          group:'group22'
        },
        {
          name:'heng',
          desc:'横',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/heng.mp3',
          group:'group22'
        },
        {
          name:'hua',
          desc:'花',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/hua.mp3',
          group:'group22'
        },
        {
          name:'huo',
          desc:'火',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/huo.mp3',
          group:'group22'
        },
        {
          name:'huai',
          desc:'坏',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/huai.mp3',
          group:'group22'
        },
        {
          name:'huan',
          desc:'欢',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group22/huan.mp3',
          group:'group22'
        },
        {
          name:'ga',
          desc:'嘎',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/ga.mp3',
          group:'group23'
        },
        {
          name:'ge',
          desc:'个',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/ge.mp3',
          group:'group23'
        },
        {
          name:'gu',
          desc:'古',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/gu.mp3',
          group:'group23'
        },
        {
          name:'kai',
          desc:'开',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/kai.mp3',
          group:'group23'
        },
        {
          name:'ke',
          desc:'可',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/ke.mp3',
          group:'group23'
        },
        {
          name:'ku',
          desc:'苦',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/ku.mp3',
          group:'group23'
        },
        {
          name:'ha',
          desc:'哈',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/ha.mp3',
          group:'group23'
        },
        {
          name:'he',
          desc:'喝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/he.mp3',
          group:'group23'
        },
        {
          name:'hu',
          desc:'湖',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/hu.mp3',
          group:'group23'
        },
        {
          name:'gao',
          desc:'高',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/gao.mp3',
          group:'group23'
        },
        {
          name:'kou',
          desc:'口',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/kou.mp3',
          group:'group23'
        },
        {
          name:'hai',
          desc:'海',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/hai.mp3',
          group:'group23'
        },
        {
          name:'gang',
          desc:'刚',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/gang.mp3',
          group:'group23'
        },
        {
          name:'keng',
          desc:'坑',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/keng.mp3',
          group:'group23'
        },
        {
          name:'huan',
          desc:'欢',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group23/huan.mp3',
          group:'group23'
        },
        {
          name:'j',
          desc:'j',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/j.mp3',
          group:'group24'
        },
        {
          name:'q',
          desc:'q',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/q.mp3',
          group:'group24'
        },
        {
          name:'x',
          desc:'x',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/x.mp3',
          group:'group24'
        },  
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/ü.mp3',
          group:'group24'
        },
        {
          name:'j',
          desc:'j',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/j.mp3',
          group:'group24'
        },
        {
          name:'q',
          desc:'q',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/q.mp3',
          group:'group24'
        },
        {
          name:'x',
          desc:'x',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/x.mp3',
          group:'group24'
        },  
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/ü.mp3',
          group:'group24'
        },
        {
          name:'j',
          desc:'j',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/j.mp3',
          group:'group24'
        },
        {
          name:'q',
          desc:'q',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/q.mp3',
          group:'group24'
        },
        {
          name:'x',
          desc:'x',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/x.mp3',
          group:'group24'
        },  
        {
          name:'ü',
          desc:'ü',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group24/ü.mp3',
          group:'group24'
        },

        {
          name:'ai',
          desc:'ai',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ai.mp3',
          group:'group25'
          },
        {
          name:'ei',
          desc:'ei',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ei.mp3',
          group:'group25'
        },
        {
          name:'ao',
          desc:'ao',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ao.mp3',
          group:'group25'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ou.mp3',
          group:'group25'
        },
        {
          name:'ai',
          desc:'ai',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ai.mp3',
          group:'group25'
          },
        {
          name:'ei',
          desc:'ei',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ei.mp3',
          group:'group25'
        },
        {
          name:'ao',
          desc:'ao',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ao.mp3',
          group:'group25'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ou.mp3',
          group:'group25'
        },
        {
          name:'ai',
          desc:'ai',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ai.mp3',
          group:'group25'
          },
        {
          name:'ei',
          desc:'ei',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ei.mp3',
          group:'group25'
        },
        {
          name:'ao',
          desc:'ao',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ao.mp3',
          group:'group25'
        },
        {
          name:'ou',
          desc:'ou',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group25/ou.mp3',
          group:'group25'
        },

        {
          name:'an',
          desc:'an',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/an.mp3',
          group:'group26'
        },
        {
          name:'en',
          desc:'en',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/en.mp3',
          group:'group26'
        },
        {
          name:'in',
          desc:'in',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/in.mp3',
          group:'group26'
        },
        {
          name:'un',
          desc:'un',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/un.mp3',
          group:'group26'
        },
        {
          name:'an',
          desc:'an',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/an.mp3',
          group:'group26'
        },
        {
          name:'en',
          desc:'en',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/en.mp3',
          group:'group26'
        },
        {
          name:'in',
          desc:'in',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/in.mp3',
          group:'group26'
        },
        {
          name:'un',
          desc:'un',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/un.mp3',
          group:'group26'
        },
        {
          name:'an',
          desc:'an',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/an.mp3',
          group:'group26'
        },
        {
          name:'en',
          desc:'en',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/en.mp3',
          group:'group26'
        },
        {
          name:'in',
          desc:'in',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/in.mp3',
          group:'group26'
        },
        {
          name:'un',
          desc:'un',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group26/un.mp3',
          group:'group26'
        },

        {
          name:'yin',
          desc:'yin',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/yin.mp3',
          group:'group27'
        },
        {
          name:'wu',
          desc:'wu',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/wu.mp3',
          group:'group27'
        },
        {
          name:'yu',
          desc:'yu',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/yu.mp3',
          group:'group27'
        },
        {
          name:'yin',
          desc:'yin',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/yin.mp3',
          group:'group27'
        },
        {
          name:'wu',
          desc:'wu',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/wu.mp3',
          group:'group27'
        },
        {
          name:'yu',
          desc:'yu',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/yu.mp3',
          group:'group27'
        },
        {
          name:'yin',
          desc:'yin',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/yin.mp3',
          group:'group27'
        },
        {
          name:'wu',
          desc:'wu',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/wu.mp3',
          group:'group27'
        },
        {
          name:'yu',
          desc:'yu',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group27/yu.mp3',
          group:'group27'
        },

        {
          name:'bie',
          desc:'别',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/bie.mp3',
          group:'group28'
        },
        {
          name:'pie',
          desc:'撇',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/pie.mp3',
          group:'group28'
        },
        {
          name:'mie',
          desc:'灭',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/mie.mp3',
          group:'group28'
        },
        {
          name:'die',
          desc:'跌',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/die.mp3',
          group:'group28'
        },
        {
          name:'tie',
          desc:'贴',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/tie.mp3',
          group:'group28'
        },
        {
          name:'nie',
          desc:'捏',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/nie.mp3',
          group:'group28'
        },
        {
          name:'lie',
          desc:'列',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/lie.mp3',
          group:'group28'
        },
        {
          name:'jie',
          desc:'接',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/jie.mp3',
          group:'group28'
        },
        {
          name:'xie',
          desc:'写',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/xie.mp3',
          group:'group28'
        },
        {
          name:'qie',
          desc:'切',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/qie.mp3',
          group:'group28'
        },
        {
          name:'yue',
          desc:'月',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/yue.mp3',
          group:'group28'
        },
        {
          name:'jue',
          desc:'绝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/jue.mp3',
          group:'group28'
        },
        {
          name:'xue',
          desc:'学',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/xue.mp3',
          group:'group28'
        },
        {
          name:'que',
          desc:'却',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/que.mp3',
          group:'group28'
        },
        {
          name:'nue',
          desc:'虐',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group28/nue.mp3',
          group:'group28'
        },

        {
          name:'zh',
          desc:'zh',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/zh.mp3',
          group:'group29'
        },
        {
          name:'ch',
          desc:'ch',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/ch.mp3',
          group:'group29'
        },
        {
          name:'sh',
          desc:'sh',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/sh.mp3',
          group:'group29'
        },
        {
          name:'an',
          desc:'an',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/an.mp3',
          group:'group29'
        },
        {
          name:'en',
          desc:'en',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/en.mp3',
          group:'group29'
        },
        {
          name:'zh',
          desc:'zh',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/zh.mp3',
          group:'group29'
        },
        {
          name:'ch',
          desc:'ch',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/ch.mp3',
          group:'group29'
        },
        {
          name:'sh',
          desc:'sh',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/sh.mp3',
          group:'group29'
        },
        {
          name:'an',
          desc:'an',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/an.mp3',
          group:'group29'
        },
        {
          name:'en',
          desc:'en',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group29/en.mp3',
          group:'group29'
        },
        {
          name:'ma',
          desc:'妈',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/ma.mp3',
          group:'group30'
        },
        {
          name:'fei',
          desc:'飞',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/fei.mp3',
          group:'group30'
        },
        {
          name:'lu',
          desc:'路',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/lu.mp3',
          group:'group30'
        },
        {
          name:'zhong',
          desc:'中',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/zhong.mp3',
          group:'group30'
        },
        {
          name:'shu',
          desc:'书',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/shu.mp3',
          group:'group30'
        },
        {
          name:'chi',
          desc:'吃',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/chi.mp3',
          group:'group30'
        },
        {
          name:'ri',
          desc:'日',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/ri.mp3',
          group:'group30'
        },
        {
          name:'zao',
          desc:'早',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/zao.mp3',
          group:'group30'
        },
        {
          name:'ci',
          desc:'次',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/ci.mp3',
          group:'group30'
        },
        {
          name:'si',
          desc:'丝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/si.mp3',
          group:'group30'
        },
        {
          name:'bao',
          desc:'包',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/bao.mp3',
          group:'group30'
        },
        {
          name:'deng',
          desc:'灯',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/deng.mp3',
          group:'group30'
        },
        {
          name:'gou',
          desc:'狗',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/gou.mp3',
          group:'group30'
        },
        {
          name:'kuai',
          desc:'快',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/kuai.mp3',
          group:'group30'
        },
        {
          name:'hua',
          desc:'花',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group30/hua.mp3',
          group:'group30'
        },
        {
          name:'lan',
          desc:'蓝',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/lan.mp3',
          group:'group31'
        },
        {
          name:'ping',
          desc:'平',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/ping.mp3',
          group:'group31'
        },
        {
          name:'xue',
          desc:'雪',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/xue.mp3',
          group:'group31'
        },
        {
          name:'ming',
          desc:'明',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/ming.mp3',
          group:'group31'
        },
        {
          name:'tian',
          desc:'天',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/tian.mp3',
          group:'group31'
        },
        {
          name:'yue',
          desc:'月',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/yue.mp3',
          group:'group31'
        },
        {
          name:'feng',
          desc:'风',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/feng.mp3',
          group:'group31'
        },
        {
          name:'hai',
          desc:'海',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/hai.mp3',
          group:'group31'
        },
        {
          name:'shan',
          desc:'山',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/shan.mp3',
          group:'group31'
        },
        {
          name:'guo',
          desc:'国',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/guo.mp3',
          group:'group31'
        },
        {
          name:'ren',
          desc:'人',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/ren.mp3',
          group:'group31'
        },
        {
          name:'shui',
          desc:'水',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/shui.mp3',
          group:'group31'
        },
        {
          name:'mu',
          desc:'木',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/mu.mp3',
          group:'group31'
        },
        {
          name:'huo',
          desc:'火',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/huo.mp3',
          group:'group31'
        },
        {
          name:'tu',
          desc:'土',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/pinyin/group31/tu.mp3',
          group:'group31'
        },
          
      ],
      isReverseMode: false,
      showHint: false,
      typedText: '',
      currentKeywordIndex: 0, // 添加当前词汇索引
    }
  },
  methods: {
    generateNewLetter() {
      // 从当前分组中选择下一个词汇
      const groupWords = this.keywords.filter(word => word.group === this.targetGroup);


      
      // 如果当前索引超出范围，重置为0
      if (this.currentKeywordIndex >= groupWords.length) {
        this.currentKeywordIndex = 0;
      }

      this.combinations = groupWords.length;
      
      // 按顺序选择词汇
      this.currentKeyword = groupWords[this.currentKeywordIndex];
      this.currentKeywordIndex++;
      
      // 设置目标和提示
      this.targetLetters = this.currentKeyword.name;
      this.currentIndex = 0;
      this.currentLetter = this.targetLetters[0];
      
      // 重置状态
      this.isCorrect = false;
      this.isWrong = false;
      this.showHint = false;
    },

    playCurrentAudio() {
      if (this.currentKeyword && this.currentKeyword.audioUrl) {
        const audio = new Audio(this.currentKeyword.audioUrl);
        audio.play();
      }
    },

    handleKeyPress(event) {
      if (!this.gameStarted || this.showComplete) return
      
      // 添加调试信息
      console.log('按下的键:', event.key, '当前期望字母:', this.currentLetter, 'keyCode:', event.keyCode)
      
      // 定义需要忽略的修饰键列表
      const modifierKeys = [
        'CapsLock', 'Shift', 'Control', 'Alt', 'Meta', 'Tab', 'Escape', 
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown', 'Insert', 'Delete',
        'NumLock', 'ScrollLock', 'Pause', 'PrintScreen',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ]
      
      // 播放音频功能（空格键）
      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault()
        if(event.code === 'Space' ){
          this.playCurrentAudio()
        }
        return
      }
      
      // 如果按下的是修饰键，直接返回，不计入尝试次数
      if (modifierKeys.includes(event.key)) {
        return
      }
      
      // 处理反向模式
      if (this.isReverseMode) {
        if (event.key === 'Backspace') {
          // 处理退格键
          this.typedText = this.typedText.slice(0, -1);
          return;
        } else if (event.key === 'Enter') {
          // 处理回车键，验证答案
          this.totalAttempts++;
          
          if (this.typedText.toLowerCase() === this.targetLetters.toLowerCase()) {
            this.handleReverseCorrectAnswer();
          } else {
            this.handleReverseWrongAnswer();
          }

          this.isReverseMode = false;//
          
          return;
        } else if (event.key.length === 1) {
          // 普通字符输入
          this.typedText += event.key;
        }
        
        return;
      }
      
      // 正向模式的逻辑
      this.totalAttempts++
      
      // 特殊处理：当前字母是'ü'时，可以使用'v'键输入
      if (this.currentLetter === 'ü' && event.key === 'v') {
        this.handleCorrectKey()
      }
      // 严格的大小写字母处理逻辑
      else if (event.key === this.currentLetter) {
        this.handleCorrectKey()
      } else {
        this.handleWrongKey()
      }
      
      this.accuracy = Math.round((this.correctAttempts / this.totalAttempts) * 100)
    },

    handleCorrectKey() {
      this.isCorrect = true
      this.correctAttempts++
      
      this.showScoreChange(10)
      this.score += 10
      
      // 添加音效
      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/correct.mp3')
      audio.play()

      // 添加特效触发
      this.$nextTick(() => {
        const scoreElement = document.querySelector('.floating-stats .stat-value-effect')
        if (scoreElement) {
          scoreElement.classList.add('active')
          setTimeout(() => {
            scoreElement.classList.remove('active')
          }, 700)
        }
      })
      
      this.currentIndex++
      
      if (this.currentIndex >= this.targetLetters.length) {
        const progressIncrement = 100 / this.combinations
        this.progress += progressIncrement

        if (this.progress >= 99) {
          this.completeLevelAndSave()
        } else {
          setTimeout(() => {
            this.generateNewLetter()
          }, 300)
        }
      } else {
        this.currentLetter = this.targetLetters[this.currentIndex]
      }

      setTimeout(() => {
        this.isCorrect = false
      }, 300)
    },

    handleWrongKey() {
      this.isWrong = true
      
      this.showScoreChange(-5)
      this.score = Math.max(0, this.score - 5)

      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/fail.mp3')
      audio.play()

      setTimeout(() => {
        this.isWrong = false
      }, 300)
    },

    completeLevelAndSave() {
      const completedLevels = JSON.parse(localStorage.getItem('completedLevels') || '[]')
      if (!completedLevels.includes(this.levelId)) {
        completedLevels.push(this.levelId)
        localStorage.setItem('completedLevels', JSON.stringify(completedLevels))
      }
      
      // 计算练习时间和打字速度
      const endTime = new Date()
      this.totalTime = (endTime - this.startTime) / 1000
      this.typingSpeed = Math.round((this.correctAttempts / (this.totalTime / 60)))
      
      // 根据得分和正确率设置结果消息和图片
      if (this.score >= 300 && this.accuracy >= 90) {
        this.resultMessage = '太棒了！你掌握了这些词汇！'
        this.resultImage = this.resultImages.excellent
      } else if (this.score >= 200 && this.accuracy >= 80) {
        this.resultMessage = '不错的进步！继续练习这些词汇！'
        this.resultImage = this.resultImages.good
      } else {
        this.resultMessage = '坚持练习，词汇需要多次重复记忆！'
        this.resultImage = this.resultImages.keepTrying
      }
      
      this.showComplete = true
    },

    exitPractice() {
      this.$router.back()
    },

    restartLevel() {
      this.score = 0
      this.totalAttempts = 0
      this.correctAttempts = 0
      this.accuracy = 100
      this.progress = 0
      this.showComplete = false
      this.generateNewLetter()
      this.$el.focus()
      this.startTime = new Date()
      
      // 重置其他状态
      this.isCorrect = false
      this.isWrong = false
      this.scoreChange = 0
      this.typingSpeed = 0
      
      // 确保游戏状态为开始
      this.gameStarted = true
      
      // 清除任何可能存在的计时器
      if (this.scoreChangeTimeout) {
        clearTimeout(this.scoreChangeTimeout)
      }
    },

    showScoreChange(change) {
      this.scoreChange = change
      
      if (this.scoreChangeTimeout) {
        clearTimeout(this.scoreChangeTimeout)
      }
      
      this.scoreChangeTimeout = setTimeout(() => {
        this.scoreChange = 0
      }, 1000)
    },

    loadRouteParams() {
      // 从路由参数获取信息
      const { group, title, instruction, levelId, nextLevel } = this.$route.query
      
      // 设置目标分组
      this.targetGroup = group || 'group1'
      
      this.title = title || '词汇'
      this.instruction = instruction || '按空格键可以听发音'
      this.levelId = levelId || 'keywords1'
      this.nextLevel = nextLevel || '/code-classroom/typing'
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.round(seconds % 60)
      return `${minutes}分${remainingSeconds}秒`
    },

    closeReport() {
      this.showComplete = false
      this.$router.back()
    },

    toggleMode() {
      this.isReverseMode = !this.isReverseMode;
      this.typedText = '';
      this.showHint = false;
      // 保存当前模式到localStorage
      //localStorage.setItem('typingPracticeMode', this.isReverseMode ? 'reverse' : 'forward');
      this.generateNewLetter();
    },

    toggleHint() {
      this.showHint = !this.showHint
    },

    handleReverseCorrectAnswer() {
      this.isCorrect = true;
      this.correctAttempts++;
      
      this.showScoreChange(50); // 完整单词给予更多分数
      this.score += 50;
      
      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/correct.mp3');
      audio.play();
      
      const progressIncrement = 100 / this.combinations;
      this.progress += progressIncrement;
      
      if (this.progress >= 100) {
        this.completeLevelAndSave();
      } else {
        setTimeout(() => {
          this.generateNewLetter();
        }, 800);
      }
      
      setTimeout(() => {
        this.isCorrect = false;
      }, 800);
    },
    
    handleReverseWrongAnswer() {
      this.isWrong = true;
      
      this.showScoreChange(-15); // 错误单词扣除更多分数
      this.score = Math.max(0, this.score - 15);
      
      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/fail.mp3');
      audio.play();
      
      setTimeout(() => {
        this.isWrong = false;
        this.typedText = ''; // 清空输入以便重新输入
      }, 800);
    },
    // 添加判断是否需要Shift键的方法
    isShiftRequired(char) {
      return /[A-Z!@#$%^&*()_+{}|:<>?]/.test(char);
    },
    selectNextWord() {
      // 从当前分组中选择下一个词汇
      const groupWords = this.keywords.filter(word => word.group === this.targetGroup);
      
      // 如果当前索引超出范围，重置为0
      if (this.currentKeywordIndex >= groupWords.length) {
        this.currentKeywordIndex = 0;
      }
      
      // 按顺序选择词汇
      this.currentKeyword = groupWords[this.currentKeywordIndex];
      this.currentKeywordIndex++;
      
      // 重置输入状态
      this.targetLetters = this.currentKeyword.name;
      this.currentIndex = 0;
      this.currentLetter = this.targetLetters[0];
      this.isCorrect = false;
      this.isWrong = false;
    },
  },
  mounted() {
    this.loadRouteParams();
    
    // 从localStorage读取之前保存的练习模式
    //const savedMode = localStorage.getItem('typingPracticeMode');
    //if (savedMode === 'reverse') {
    //  this.isReverseMode = true;
    //}
    
    this.generateNewLetter();
    this.$el.focus();
    this.startTime = new Date();
    
    // 将焦点设置到游戏容器
    setTimeout(() => {
      this.$el.focus();
    }, 100);
  },
  // 添加路由参数变化时的处理
  watch: {
    '$route'() {
      this.loadRouteParams()
      this.restartLevel()
    }
  }
}
</script>

<style scoped>
.game-container {
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  outline: none;
  background: #F8FBFF;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
}

.game-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 20px 25px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(32, 107, 196, 0.08);
  margin-bottom: 30px;
  transition: all 0.3s ease;
}

.header-content {
  margin-bottom: 15px;
}

.game-header h1 {
  font-size: 30px;
  color: #1976D2;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
}

.instruction {
  font-size: 16px;
  color: #546E7A;
  margin: 10px 0;
  letter-spacing: 0.5px;
}

/* 进度条样式优化 */
.progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 15px;
}

.level-info {
  font-size: 14px;
  color: #546E7A;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500;
}

.progress {
  height: 10px;
  background: #E3F2FD;
  border-radius: 6px;
  overflow: hidden;
  flex-grow: 1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 6px;
  box-shadow: 0 1px 5px rgba(76, 175, 80, 0.3);
}

/* 统计区域样式优化 */
.stats-container {
  display: flex;
  justify-content: center;
  gap: 25px;
  margin-bottom: 30px;
}

/* 添加悬浮统计面板样式 */
.floating-stats {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 100;
}

.stat-card {
  position: relative;
  animation: floatIn 0.5s ease-out forwards;
}

.card-decoration {
  position: absolute;
  left: -10px;
  top: 0;
  height: 100%;
  width: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: -1;
}

.decoration-circle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
  margin-top: 10px;
  box-shadow: 0 0 10px rgba(25, 118, 210, 0.4);
}

.decoration-line {
  width: 2px;
  flex-grow: 1;
  background: linear-gradient(to bottom, #1976D2, rgba(100, 181, 246, 0.1));
  margin-top: 5px;
}

.stat-card.accuracy .decoration-circle {
  background: linear-gradient(135deg, #4CAF50, #8BC34A);
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.4);
}

.stat-card.accuracy .decoration-line {
  background: linear-gradient(to bottom, #4CAF50, rgba(139, 195, 74, 0.1));
}

.floating-stats .stat-box {
  max-width: 180px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 15px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 15px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.floating-stats .stat-box:hover {
  transform: translateX(-5px) scale(1.03);
  box-shadow: 0 15px 30px rgba(25, 118, 210, 0.15), 0 0 0 3px rgba(25, 118, 210, 0.1);
  border-color: rgba(25, 118, 210, 0.3);
}

.floating-stats .stat-box.accuracy:hover {
  box-shadow: 0 15px 30px rgba(76, 175, 80, 0.15), 0 0 0 3px rgba(76, 175, 80, 0.1);
  border-color: rgba(76, 175, 80, 0.3);
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulsate {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
}

.floating-stats .stat-value {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;
  transition: all 0.3s ease;
}

.floating-stats .stat-box:hover .stat-value {
  transform: scale(1.1);
  text-shadow: 0 2px 10px rgba(25, 118, 210, 0.3);
}

.floating-stats .accuracy .stat-value {
  background: linear-gradient(135deg, #4CAF50, #8BC34A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.floating-stats .accuracy:hover .stat-value {
  text-shadow: 0 2px 10px rgba(76, 175, 80, 0.3);
}

.floating-stats .icon-circle {
  width: 40px;
  height: 40px;
  font-size: 20px;
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.15), rgba(100, 181, 246, 0.25));
  box-shadow: 0 5px 12px rgba(25, 118, 210, 0.15);
  position: relative;
  overflow: hidden;
}

.floating-stats .icon-circle::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.7) 0%, transparent 60%);
  opacity: 0;
  transform: rotate(30deg);
  transition: all 0.6s ease;
}

.floating-stats .stat-box:hover .icon-circle::after {
  opacity: 1;
  transform: rotate(0deg);
}

.floating-stats .accuracy .icon-circle {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(139, 195, 74, 0.25));
  box-shadow: 0 5px 12px rgba(76, 175, 80, 0.15);
}

.floating-stats .accuracy-bar {
  height: 6px;
  background: rgba(227, 242, 253, 0.5);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 6px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.floating-stats .accuracy-fill {
  height: 100%;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.5s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.floating-stats .accuracy-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.1) 100%);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.stat-box {
  background: white;
  padding: 18px 22px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(25, 118, 210, 0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 220px;
  position: relative;
  overflow: hidden;
}

.stat-box:hover {
  transform: translateY(-5px);
  border-color: rgba(25, 118, 210, 0.2);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.08);
  background-color: #FAFCFF;
}

.stat-box::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #1976D2, #64B5F6);
  opacity: 0;
  transition: opacity 0.4s;
}

.stat-box:hover::after {
  opacity: 1;
}

/* 图标样式优化 */
.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(100, 181, 246, 0.2));
  font-size: 24px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(25, 118, 210, 0.1);
}

.stat-box:hover .icon-circle {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 6px 15px rgba(25, 118, 210, 0.15);
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 26px;
  font-weight: bold;
  color: #1976D2;
  transition: all 0.3s ease;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.stat-box:hover .stat-value {
  color: #1565C0;
  transform: scale(1.05);
}

.stat-label {
  font-size: 14px;
  color: #546E7A;
  margin-top: 4px;
  font-weight: 500;
}

/* 得分变化动画优化 */
.score-change {
  position: absolute;
  top: 5px;
  right: 10px;
  font-size: 18px;
  font-weight: bold;
  animation: fadeUp 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  opacity: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  z-index: 5;
}

.score-change.positive {
  color: #4CAF50;
}

.score-change.negative {
  color: #F44336;
}

@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  20% {
    opacity: 1;
    transform: translateY(-5px);
  }
  80% {
    opacity: 1;
    transform: translateY(-15px);
  }
  100% {
    opacity: 0;
    transform: translateY(-25px);
  }
}

/* 得分特效 */
.stat-value-effect {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 50%;
  z-index: -1;
  opacity: 0;
  pointer-events: none;
}

.stat-value-effect.active {
  animation: pulse-ring 0.8s cubic-bezier(0.455, 0.03, 0.515, 0.955) forwards;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
    opacity: 0.5;
  }
  80% {
    transform: scale(1.5);
    box-shadow: 0 0 0 15px rgba(25, 118, 210, 0);
    opacity: 0;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.stat-value-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.accuracy-bar {
  height: 6px;
  background: #E3F2FD;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.accuracy-fill {
  height: 100%;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.5s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.accuracy-fill.high {
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
}

.accuracy-fill.medium {
  background: linear-gradient(90deg, #FF9800, #FFC107);
}

.accuracy-fill.low {
  background: linear-gradient(90deg, #F44336, #FF5722);
}

/* 打字区域样式优化 */
.typing-area {
  background: white;
  padding: 35px;
  border-radius: 16px;
  margin: 20px 0 30px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(25, 118, 210, 0.1);
  position: relative;
  overflow: hidden;
}

.typing-area::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(200, 230, 255, 0.2), transparent 70%);
  pointer-events: none;
}

.typing-area.correct {
  background: linear-gradient(to bottom, #e8f5e9, white);
  border-color: #A5D6A7;
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.15);
}

.typing-area.wrong {
  background: linear-gradient(to bottom, #ffebee, white);
  border-color: #FFCDD2;
  box-shadow: 0 10px 30px rgba(244, 67, 54, 0.15);
}

.keyword-info {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.keyword-name {
  font-size: 36px;
  font-weight: bold;
  color: #1976D2;
  margin-bottom: 8px;
  text-shadow: 0 2px 5px rgba(25, 118, 210, 0.2);
  letter-spacing: 1px;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.keyword-desc {
  font-size: 18px;
  color: #546E7A;
  margin-bottom: 15px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.audio-hint {
  font-size: 14px;
  background-color: #EEF2F6;
  padding: 6px 12px;
  border-radius: 50px;
  color: #546E7A;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.audio-hint:hover {
  background-color: #E3F2FD;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.audio-hint::before {
  content: "🔊";
  margin-right: 8px;
  font-size: 16px;
}

.target-letters {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 25px;
  letter-spacing: 12px;
  line-height: 1.6;
}

.target-letters span {
  display: inline-block;
  padding: 0 10px;
  color: #78909C;
  position: relative;
  transition: all 0.3s ease;
}

.target-letters span.current {
  color: #1976D2;
  text-shadow: 0 1px 5px rgba(25, 118, 210, 0.3);
  border-bottom: 3px solid #1976D2;
  animation: pulse 1.2s infinite;
  transform: scale(1.1);
}

.target-letters span.completed {
  color: #4CAF50;
  text-shadow: 0 1px 3px rgba(76, 175, 80, 0.2);
}

.letter-instruction {
  font-size: 24px;
  color: #546E7A;
  margin: 20px 0;
  font-weight: 500;
}

.letter-highlight {
  position: relative;
  display: inline-block;
  font-size: 32px;
  color: #1976D2;
  font-weight: bold;
  text-shadow: 0 2px 5px rgba(25, 118, 210, 0.2);
  transition: all 0.3s ease;
  animation: highlight-pulse 2s infinite;
}

@keyframes highlight-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.letter-highlight.uppercase {
  color: #FF5722;
  text-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
}

.letter-highlight.special-char {
  color: #FF5722;
  text-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
}

.uppercase-hint {
  position: absolute;
  top: -18px;
  right: -18px;
  background: linear-gradient(135deg, #FF5722, #FF9800);
  color: white;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: bold;
  transform: rotate(10deg);
  box-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
  animation: float 3s infinite ease-in-out;
}

@keyframes float {
  0% { transform: rotate(10deg) translateY(0); }
  50% { transform: rotate(10deg) translateY(-5px); }
  100% { transform: rotate(10deg) translateY(0); }
}

/* 键盘显示区域优化 */
.practice-keyboard {
  background: linear-gradient(to bottom, #37474F, #263238);
  padding: 25px;
  border-radius: 16px;
  margin: 20px auto 30px;
  width: fit-content;
  transform: perspective(1000px) rotateX(5deg);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 5px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid #1e2b38;
  transition: all 0.3s ease;
}

.practice-keyboard:hover {
  transform: perspective(1000px) rotateX(2deg);
}

.practice-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  position: relative;
}

/* 错开排列，模拟真实键盘 */
.practice-row:nth-child(2) {
  padding-left: 12px;
}

.practice-row:nth-child(3) {
  padding-left: 18px;
}

.practice-row:nth-child(4) {
  padding-left: 25px;
}

.practice-key {
  width: 44px;
  height: 44px;
  background: linear-gradient(to bottom, #455A64, #37474F);
  color: white;
  border: none;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
  box-shadow: 0 4px 0 #1e2b38, 0 6px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  padding: 4px 2px;
}

.practice-key::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.12), rgba(255,255,255,0));
  border-radius: 7px 7px 0 0;
}

.practice-key.active {
  background: linear-gradient(to bottom, #1976D2, #1565C0);
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(0, 0, 0, 0.2);
}

.practice-key.active-shift {
  background: linear-gradient(to bottom, #FF5722, #E64A19);
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(0, 0, 0, 0.2);
  animation: pulsate 1s infinite;
}

@keyframes pulsate {
  0% { opacity: 1; box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(255, 87, 34, 0.4); }
  50% { opacity: 0.8; box-shadow: 0 0 10px rgba(255, 87, 34, 0.6), 0 2px 5px rgba(255, 87, 34, 0.4); }
  100% { opacity: 1; box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(255, 87, 34, 0.4); }
}

/* 特殊键位的宽度优化 */
.backspace-key {
  width: 75px;
}

.tab-key {
  width: 65px;
}

.backslash-key {
  width: 55px;
}

.caps-key {
  width: 80px;
}

.enter-key {
  width: 85px;
}

.shift-key {
  width: 100px;
}

.shift-key-right {
  width: 100px;
}

.ctrl-key, .alt-key, .win-key, .menu-key {
  width: 55px;
}

.space-key {
  width: 280px;
  font-size: 15px;
  background: linear-gradient(to bottom, #546E7A, #455A64);
}

.space-key.active {
  background: linear-gradient(to bottom, #1976D2, #1565C0);
}

/* 控制按钮优化 */
.control-buttons {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.exit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 25px;
  background: linear-gradient(to bottom, #f44336, #d32f2f);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
  font-size: 16px;
  box-shadow: 0 4px 10px rgba(244, 67, 54, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.exit-button:hover {
  background: linear-gradient(to bottom, #e53935, #c62828);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(244, 67, 54, 0.4);
}

.exit-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
}

.button-icon {
  margin-right: 10px;
  font-size: 1.2em;
}

/* 练习报告弹窗优化 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.report-content {
  background: #ffffff;
  padding: 0;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  position: relative;
}

@keyframes bounceIn {
  0% { transform: scale(0.8); opacity: 0; }
  70% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

.report-content h2 {
  font-size: 28px;
  margin: 0;
  padding: 25px 0;
  color: #ffffff;
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  border-radius: 24px 24px 0 0;
  position: relative;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.report-content h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px;
  position: relative;
}

.report-stats::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="10" cy="10" r="2" fill="%23f0f0f0"/><circle cx="30" cy="10" r="2" fill="%23f0f0f0"/><circle cx="50" cy="10" r="2" fill="%23f0f0f0"/><circle cx="70" cy="10" r="2" fill="%23f0f0f0"/><circle cx="90" cy="10" r="2" fill="%23f0f0f0"/><circle cx="10" cy="30" r="2" fill="%23f0f0f0"/><circle cx="30" cy="30" r="2" fill="%23f0f0f0"/><circle cx="50" cy="30" r="2" fill="%23f0f0f0"/><circle cx="70" cy="30" r="2" fill="%23f0f0f0"/><circle cx="90" cy="30" r="2" fill="%23f0f0f0"/><circle cx="10" cy="50" r="2" fill="%23f0f0f0"/><circle cx="30" cy="50" r="2" fill="%23f0f0f0"/><circle cx="50" cy="50" r="2" fill="%23f0f0f0"/><circle cx="70" cy="50" r="2" fill="%23f0f0f0"/><circle cx="90" cy="50" r="2" fill="%23f0f0f0"/><circle cx="10" cy="70" r="2" fill="%23f0f0f0"/><circle cx="30" cy="70" r="2" fill="%23f0f0f0"/><circle cx="50" cy="70" r="2" fill="%23f0f0f0"/><circle cx="70" cy="70" r="2" fill="%23f0f0f0"/><circle cx="90" cy="70" r="2" fill="%23f0f0f0"/><circle cx="10" cy="90" r="2" fill="%23f0f0f0"/><circle cx="30" cy="90" r="2" fill="%23f0f0f0"/><circle cx="50" cy="90" r="2" fill="%23f0f0f0"/><circle cx="70" cy="90" r="2" fill="%23f0f0f0"/><circle cx="90" cy="90" r="2" fill="%23f0f0f0"/></svg>');
  z-index: -1;
  opacity: 0.3;
}

.stat-item {
  padding: 20px 15px;
  background: #f8f9ff;
  border-radius: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.stat-item:nth-child(1) { animation-delay: 0.1s; }
.stat-item:nth-child(2) { animation-delay: 0.2s; }
.stat-item:nth-child(3) { animation-delay: 0.3s; }
.stat-item:nth-child(4) { animation-delay: 0.4s; }

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: #e0e7ff;
  background: #f0f7ff;
}

.stat-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4A6FFF, #9C4AFF);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 3px 3px 0 0;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  font-weight: 600;
  text-align: center;
}

.stat-label::before {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #4A6FFF;
  text-shadow: 0 2px 4px rgba(74, 111, 255, 0.2);
  position: relative;
  display: inline-block;
  text-align: center;
  margin-top: 5px;
}

.keyword-summary {
  margin: 0 30px 30px;
  padding: 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
}

.keyword-summary h3 {
  color: #4A6FFF;
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 700;
}

.keyword-summary p {
  margin: 0;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.keyword-summary .highlight {
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  background-clip: text;
    -webkit-background-clip: text;
    background-clip: text;
  color: transparent;
  font-weight: 700;
  padding: 0 5px;
  position: relative;
}

.keyword-summary .highlight::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4A6FFF, #9C4AFF);
  opacity: 0.2;
  border-radius: 3px;
}

.report-message {
  margin: 0 30px 30px;
  padding: 25px 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
  text-align: center;
}

.report-message::before {
  content: '';
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: #4A6FFF;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message::after {
  content: '';
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: #9C4AFF;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message .result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.report-message img {
  width: 120px;
  height: 120px;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  animation: pulse 2s infinite ease-in-out;
  transform-origin: center;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.report-message p {
  font-size: 20px;
  color: #4A6FFF;
  font-weight: 600;
  margin: 10px 0 0;
  padding: 0;
  position: relative;
  display: block;
  text-align: center;
}

.report-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding: 0 30px 30px;
  animation: fadeIn 0.6s ease-out 0.7s forwards;
  opacity: 0;
}

.report-buttons button {
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  min-width: 140px;
}

.report-buttons button:first-child {
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  color: white;
}

.report-buttons button:first-child:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(74, 111, 255, 0.3);
}

.report-buttons button:first-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(74, 111, 255, 0.2);
}

.report-buttons button:last-child {
  background: white;
  color: #666;
  border: 2px solid #eee;
}

.report-buttons button:last-child:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.05);
  border-color: #e0e7ff;
  color: #4A6FFF;
}

.report-buttons button:last-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.03);
}

.report-buttons button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  transform: translateY(-100%);
  transition: transform 0.6s;
}

.report-buttons button:hover::before {
  transform: translateY(0);
}

/* 响应式布局优化 */
@media (max-width: 768px) {
  .game-container {
    padding: 15px;
  }
  
  .game-header {
    padding: 15px 20px;
  }
  
  .game-header h1 {
    font-size: 24px;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .stat-box {
    max-width: 100%;
    width: 100%;
  }
  
  .typing-area {
    padding: 25px 15px;
  }
  
  .keyword-name {
    font-size: 28px;
  }
  
  .keyword-desc {
    font-size: 16px;
  }
  
  .target-letters {
    font-size: 36px;
    letter-spacing: 8px;
  }
  
  .letter-instruction {
    font-size: 20px;
  }
  
  .letter-highlight {
    font-size: 26px;
  }
  
  /* 移动设备键盘布局优化 */
  .practice-keyboard {
    padding: 15px 10px;
    overflow-x: auto;
    max-width: 100%;
    transform: perspective(1000px) rotateX(3deg);
  }
  
  .practice-row {
    margin-bottom: 4px;
    flex-wrap: nowrap;
    min-width: 540px; /* 确保键盘不会被压缩太小 */
    gap: 4px;
  }
  
  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  /* 移动设备上特殊键的宽度调整 */
  .backspace-key {
    width: 60px;
  }
  
  .tab-key {
    width: 50px;
  }
  
  .backslash-key {
    width: 45px;
  }
  
  .caps-key {
    width: 65px;
  }
  
  .enter-key {
    width: 70px;
  }
  
  .shift-key, .shift-key-right {
    width: 75px;
  }
  
  .ctrl-key, .alt-key, .win-key, .menu-key {
    width: 45px;
  }
  
  .space-key {
    width: 180px;
  }
  
  .report-content {
    padding: 25px 20px;
  }
  
  .report-content h2 {
    font-size: 22px;
  }
  
  .report-stats {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .report-message img {
    width: 80px;
    height: 80px;
  }
  
  .report-message p {
    font-size: 16px;
  }
  
  .report-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .report-buttons button {
    width: 100%;
  }
}

/* 添加模式切换按钮 */
.mode-switch-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.mode-switch-button {
  padding: 12px 25px;
  background: linear-gradient(to bottom, #1976D2, #1565C0);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 16px;
  box-shadow: 0 6px 15px rgba(25, 118, 210, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.mode-switch-button.active {
  background: linear-gradient(to bottom, #1565C0, #0D47A1);
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(25, 118, 210, 0.4);
}

.mode-description {
  font-size: 16px;
  color: #546E7A;
  font-weight: 500;
}

/* 反向模式样式 */
.keyword-question {
  font-size: 22px;
  color: #546E7A;
  margin-bottom: 15px;
  font-weight: 600;
}

.keyword-name.reverse {
  font-size: 32px;
  font-weight: bold;
  color: #1976D2;
  margin-bottom: 12px;
  text-shadow: 0 2px 5px rgba(25, 118, 210, 0.2);
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  padding: 5px 15px;
  border-radius: 8px;
  border: 2px dashed rgba(25, 118, 210, 0.2);
  display: inline-block;
}

.keyword-hint {
  background-color: #FFF8E1;
  padding: 8px 15px;
  border-radius: 8px;
  margin-top: 10px;
  margin-bottom: 15px;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease;
  box-shadow: 0 3px 10px rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.hint-label {
  font-weight: 600;
  color: #F57C00;
  margin-right: 5px;
}

.hint-text {
  color: #FF9800;
  font-weight: 500;
}

.user-input {
  margin: 25px 0;
  text-align: center;
}

.input-prompt {
  display: block;
  font-size: 18px;
  color: #546E7A;
  margin-bottom: 15px;
  font-weight: 500;
}

.input-display {
  display: inline-flex;
  padding: 12px 25px;
  background: rgba(25, 118, 210, 0.05);
  border: 2px solid rgba(25, 118, 210, 0.1);
  border-radius: 8px;
  font-size: 24px;
  color: #1976D2;
  font-weight: 500;
  min-width: 200px;
  min-height: 35px;
  position: relative;
  justify-content: center;
  transition: all 0.3s ease;
}

.input-display:focus {
  border-color: rgba(25, 118, 210, 0.3);
  background: rgba(25, 118, 210, 0.08);
  box-shadow: 0 3px 10px rgba(25, 118, 210, 0.1);
}

.typed-text {
  letter-spacing: 1px;
}

.cursor-indicator {
  display: inline-block;
  width: 2px;
  height: 25px;
  background-color: #1976D2;
  margin-left: 2px;
  animation: blink 0.8s infinite;
  align-self: center;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.hint-container {
  margin-top: 20px;
}

.hint-button {
  padding: 10px 20px;
  background: linear-gradient(to bottom, #FF9800, #F57C00);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 10px rgba(255, 152, 0, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.hint-button:hover {
  background: linear-gradient(to bottom, #F57C00, #EF6C00);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(255, 152, 0, 0.4);
}

.hint-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 添加双键位按键样式 */
.key-top, .key-bottom, .key-single {
  display: block;
  line-height: 1.2;
  width: 100%;
  text-align: center;
}

.key-top {
  font-size: 11px;
  color: #FFD54F;
  margin-bottom: 2px;
  position: relative;
  top: -2px;
}

.key-bottom {
  font-size: 15px;
  position: relative;
  top: 1px;
}

.key-single {
  font-size: 16px;
}

/* 响应式布局优化 */
@media (max-width: 768px) {
  .game-container {
    padding: 15px;
  }
  
  .game-header {
    padding: 15px 20px;
  }
  
  .game-header h1 {
    font-size: 24px;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .stat-box {
    max-width: 100%;
    width: 100%;
  }
  
  .typing-area {
    padding: 25px 15px;
  }
  
  .keyword-name {
    font-size: 28px;
  }
  
  .keyword-desc {
    font-size: 16px;
  }
  
  .target-letters {
    font-size: 36px;
    letter-spacing: 8px;
  }
  
  .letter-instruction {
    font-size: 20px;
  }
  
  .letter-highlight {
    font-size: 26px;
  }
  
  /* 移动设备键盘布局优化 */
  .practice-keyboard {
    padding: 15px 10px;
    overflow-x: auto;
    max-width: 100%;
    transform: perspective(1000px) rotateX(3deg);
  }
  
  .practice-row {
    margin-bottom: 4px;
    flex-wrap: nowrap;
    min-width: 540px; /* 确保键盘不会被压缩太小 */
    gap: 4px;
  }
  
  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  /* 移动设备上特殊键的宽度调整 */
  .backspace-key {
    width: 60px;
  }
  
  .tab-key {
    width: 50px;
  }
  
  .backslash-key {
    width: 45px;
  }
  
  .caps-key {
    width: 65px;
  }
  
  .enter-key {
    width: 70px;
  }
  
  .shift-key, .shift-key-right {
    width: 75px;
  }
  
  .ctrl-key, .alt-key, .win-key, .menu-key {
    width: 45px;
  }
  
  .space-key {
    width: 180px;
  }
  
  .report-content {
    padding: 25px 20px;
  }
  
  .report-content h2 {
    font-size: 22px;
  }
  
  .report-stats {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .report-message img {
    width: 80px;
    height: 80px;
  }
  
  .report-message p {
    font-size: 16px;
  }
  
  .report-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .report-buttons button {
    width: 100%;
  }
  
  .key-top {
    font-size: 10px;
  }
  
  .key-bottom {
    font-size: 14px;
  }
  
  .key-single {
    font-size: 15px;
  }
}

.special-char-hint {
  position: absolute;
  top: -18px;
  right: -18px;
  background: linear-gradient(135deg, #FF5722, #FF9800);
  color: white;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: bold;
  transform: rotate(10deg);
  box-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
  animation: float 3s infinite ease-in-out;
}

@keyframes float {
  0% { transform: rotate(10deg) translateY(0); }
  50% { transform: rotate(10deg) translateY(-5px); }
  100% { transform: rotate(10deg) translateY(0); }
}

.mode-switch-button.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: #f5f5f5;
  border-color: #ddd;
  color: #999;
  box-shadow: none;
}

.mode-switch-button.disabled:hover {
  background-color: #f5f5f5;
  border-color: #ddd;
  color: #999;
  transform: none;
}

.mode-switch-button.disabled .button-icon {
  opacity: 0.5;
}
</style>